/**
 * Global keyboard handling. Single keys and `g` sequences come from KEYBOARD_SHORTCUTS in shared; item-specific
 * actions (note, reminder, star...) resolve through the command bar registry so each screen decides what they do.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCommandBarStore, useLayoutStore } from "./navigation";
import { useEngineQuery } from "@/engine/hooks";

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

export function isMod(e: KeyboardEvent | React.KeyboardEvent): boolean {
  return e.metaKey || e.ctrlKey;
}

/** Normalises a key event to the shortcut vocabulary used in shared/constants.ts ("Mod+K", "g h", "#", "?"). */
export function eventToKey(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("Mod");
  if (e.altKey) parts.push("Alt");
  if (e.shiftKey && e.key.length > 1) parts.push("Shift");
  const key = e.key === " " ? "Space" : e.key.length === 1 ? e.key : e.key;
  parts.push(key.length === 1 ? (parts.length ? key.toUpperCase() : key) : key);
  return parts.join("+");
}

export function useGlobalShortcuts() {
  const navigate = useNavigate();
  const pendingG = useRef<number | null>(null);
  const groups = useEngineQuery("listGroups", undefined);
  const groupsRef = useRef(groups.data);
  groupsRef.current = groups.data;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const store = useCommandBarStore.getState();
      const layout = useLayoutStore.getState();
      const key = eventToKey(e);

      // Mod+K works everywhere, even inside inputs.
      if (key === "Mod+K") {
        e.preventDefault();
        store.setOpen(!store.open);
        return;
      }
      if (store.open || layout.shortcutsOpen) return;
      if (isTypingTarget(e.target)) {
        if (e.key === "Escape") (e.target as HTMLElement).blur();
        return;
      }
      // Dialogs and open menus own the keyboard (Escape closes them, not the page).
      if (document.querySelector("[role='dialog'], [role='menu']")) return;

      // Pending `g` sequence.
      if (pendingG.current !== null) {
        window.clearTimeout(pendingG.current);
        pendingG.current = null;
        const k = e.key.toLowerCase();
        const groupIndex = /^[1-9]$/.test(k) ? Number(k) - 1 : -1;
        if (k === "h") navigate("/");
        else if (k === "p") navigate("/people");
        else if (k === "s") navigate("/people?starred=1");
        else if (k === "m") navigate("/matches");
        else if (k === "n") navigate("/notes");
        else if (k === "r") navigate("/reminders");
        else if (groupIndex >= 0) {
          const g = (groupsRef.current ?? []).filter((g) => !g.isInternal)[groupIndex];
          if (g) navigate(`/group/${g.id}`);
        } else return;
        e.preventDefault();
        return;
      }

      if (e.key === "g" && !isMod(e)) {
        pendingG.current = window.setTimeout(() => (pendingG.current = null), 1200);
        return;
      }
      if (e.key === "/" && !isMod(e)) {
        e.preventDefault();
        const box = document.querySelector<HTMLInputElement>("[data-search-box]");
        if (box) box.focus();
        else navigate("/search");
        return;
      }
      if (e.key === "?" ) {
        e.preventDefault();
        layout.setShortcutsOpen(true);
        return;
      }
      if (e.key === "[" && !isMod(e)) {
        e.preventDefault();
        layout.toggleNav();
        return;
      }
      if (e.key === "Escape") {
        navigate(-1);
        return;
      }
      // Registered single-key actions (p, n, r, l, h, c, s, m, #, w, o, e...).
      const match = store.items().find((it) => it.shortcut && it.shortcut === key);
      if (match) {
        e.preventDefault();
        match.run();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);
}
